'use client'

import React, { useMemo } from 'react'

import { useHolidayContext } from '@/src/contexts'
import { useTranslations } from 'next-intl'

import {
    buildHolidayNavTabsFromBlocks,
    buildHolidayPageBlocks
} from '@/src/utils/holidays/buildHolidayPageBlocks'
import { selectRefundTimeline } from '@/src/utils/holidays/parseRefundTimeline'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'

import HolidayPackageDesktopPage from './package'

const HolidayPackageDesktopContainer = ({ domainCountry }: { domainCountry: string }) => {
    const { packageData } = useHolidayContext()

    const t = useTranslations('holidaysPage')

    // Blocks are the page: several API sections share one block, and the tabs are built from the
    // blocks so the bar can never offer a section the body does not render.
    const blocks = useMemo(
        () =>
            packageData
                ? buildHolidayPageBlocks(packageData.sections, {
                      labels: {
                          inclusions: { title: t('inclusionsTitle'), navLabel: t('inclusionsNavLabel') },
                          beforeYouBook: {
                              title: t('beforeYouBookTitle'),
                              navLabel: t('beforeYouBookTitle')
                          }
                      }
                  })
                : [],
        [packageData, t]
    )
    const navTabs = useMemo(() => buildHolidayNavTabsFromBlocks(blocks), [blocks])

    // Resolved here, not per section: which schedule wins depends on the whole document.
    const refundTimeline = useMemo(
        () => (packageData ? selectRefundTimeline(packageData.sections) : null),
        [packageData]
    )

    if (!packageData) return null

    return (
        <ErrorBoundaryWrapper identifier="holidays-package-desktop">
            <HolidayPackageDesktopPage
                holidayPackage={packageData}
                blocks={blocks}
                navTabs={navTabs}
                collectionLabel={packageData.collectionLabel}
                refundTimeline={refundTimeline}
                domainCountry={domainCountry}
            />
        </ErrorBoundaryWrapper>
    )
}

export default HolidayPackageDesktopContainer
